// src/context/SocketContext.js
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { initWebSocket, SOCKET } from './initWebSocket';
import { useDispatch, useSelector } from 'react-redux';
import {
  setBotWaitLoader
} from '../../state/actions/loader';
import { getAuthData, getSessinIdFromLS } from '../storage/LocalStorage';
import { createConversationId } from '../../utils/conversationUtils';
import { appType } from '../../utils/config/configHelpers'
import { setCrewMattersConversationId, setInializeSocket } from '../../state/actions/socket';
import { currentTimeStamp } from '../../utils/moment';
import { startConversation } from './socketEvents';
import { getAccessToken } from '../../utils/tokenUtils';
import { getPageName } from '../../utils/general';
import { isCrewMattersDomain } from '../../utils/DomainsHelper';

const SocketContext = createContext(null);

export const SocketProvider = ({ lastLoggedInDomain, children }) => {
  const [socketParams, setSocketParams] = useState(null);
  const [connected, setConnected] = useState(false);
  const messageQueue = useRef([]);
  const intervalRef = useRef(null);

  const dispatch = useDispatch();
  const pathname = getPageName();
  const sessionIdfromLS = getSessinIdFromLS();

  const userDetail = getAuthData();
  const userData = userDetail?.user || {};
  const sessionId = sessionIdfromLS || userDetail.auth?.sessionId;
 const socketConnectionStatus = useSelector((state) => state.socket.socketConnectionStatus);
 const connectionId = useSelector((state) => state.user.connectionId);
  const selectedBotDetails = useSelector((state) => state.domain.selectedBotDetails);
  const selectedDomainDetails = useSelector((state) => state.domain.selectedDomainDetails);
  const isDefaultHomePageBot = useSelector((state) => state.bot.isDefaultHomePageBot);
  const initializeSocket = useSelector((state) => state.socket.initializeSocket);
  const withoutInitSocket = useSelector((state) => state.socket.withoutInitSocket);
  const domainState = useSelector((state) => state.domain.domainState);
  
  useEffect(() => {
    dispatch(setInializeSocket(true));
  }, [])

  // Initialize WebSocket
  const initializeWebSocket = async () => {
    const accessToken = getAccessToken()
    const params = {
      email: userData.emailAddress || "",
      sessionId: sessionId,
      appType: appType,
      platform: "web",
      accessToken
    };

    console.log("params sent to initWebSocket", params);

    initWebSocket(params, sessionId);
    setSocketParams(params);
    dispatch(setInializeSocket(false));
  };

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0 && initializeSocket) {
      initializeWebSocket();
    }
  }, [userData]);


  // Initialize WebSocket for bot chat
  const initializeWebSocketForBotChat = async () => {
    const currentDomain = selectedDomainDetails?.userDomain || lastLoggedInDomain?.userDomain || '';
    const landingBotId = selectedBotDetails?.botId || lastLoggedInDomain?.landingBotId || '';
    const accessToken = getAccessToken();

    if (!landingBotId) {
      console.log("%c Unable to get last login landingBotId", "color: red;");
      dispatch(setBotWaitLoader(false));
      return;
    }

    const conversationId = await createConversationId(userData.userId, landingBotId);
    const params = {
      email: userData.emailAddress || "",
      sessionId: sessionId,
      appType: appType,
      platform: "web",
      conversationId,
      botId: landingBotId,
      userDomain: currentDomain,
      userId: userData.userId,
      accessToken: accessToken
    };

    console.log("params sent to initWebSocket for bot chat", params, currentTimeStamp());
    console.log("withoutInitSocket", withoutInitSocket)
    if (!withoutInitSocket) {
   initWebSocket(params, sessionId, true);
    } else {
    startConversation(userData.userId, params);
   }
    setSocketParams(params);
    localStorage.setItem("botParams", JSON.stringify(params));
    dispatch(setInializeSocket(false));
  };

  useEffect(() => {
    if (selectedBotDetails && !selectedBotDetails.conversational && !selectedBotDetails.systemBot && pathname === "Home" && !isDefaultHomePageBot) {
      initializeWebSocketForBotChat();
    }
  }, [selectedBotDetails, lastLoggedInDomain]);

   useEffect(() => {
    const startConversationForCrewMatters = async () => {
      const accessToken = getAccessToken();
      const currentDomainDetails = selectedDomainDetails || lastLoggedInDomain || '';
      const conversationId = await createConversationId(userData.userId, currentDomainDetails?.defaultHomePageBotId);
      const landingBotId =  lastLoggedInDomain?.defaultHomePageBotId || currentDomainDetails?.defaultHomePageBotId || '';
      const params = {
      email: userData.emailAddress || "",
      sessionId: sessionId,
      appType: appType,
      platform: "web",
      conversationId,
      botId: landingBotId,
      userId: userData.userId,
      accessToken: accessToken
    };
      dispatch(setCrewMattersConversationId(conversationId));
      dispatch(setBotWaitLoader(true));
      startConversation(userData.userId, params);
    };
    if (selectedDomainDetails?.userDomain === "isleofman" && pathname === "Home" && isDefaultHomePageBot && selectedDomainDetails?.defaultHomePageBotId) {
      startConversationForCrewMatters();
    }
  }, [ isDefaultHomePageBot, selectedDomainDetails]);

  // Manual disconnect function
  const disconnectSocket = () => {
    const socket = SOCKET.socketClient;
    if (socket) {
      // Remove listeners for current email
      if (socketParams?.email) {
        socket.off(socketParams.email);
      }
      // Clear interval that processes the queue
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Close the socket connection
      if (socket.connected) {
        socket.disconnect();
      }
      setConnected(false);
      console.log("Socket manually disconnected");
    }
  };

  

  return (
    <SocketContext.Provider value={{ socket: SOCKET.socketClient, connected, disconnectSocket }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
